// apps/frontend/src/components/feedback/ExistingUserFeedbackBanner.tsx
// Banner discret pour solliciter l'avis de TOUS les users authentifies
// (anciens et nouveaux). Les triggers contextuels (FIRST_PUBLISH,
// SHOP_CUSTOMIZED, FREQUENT_VISIT) restent en place ; ce banner sert de
// canal additionnel pour ceux qui n'auraient pas declenche de trigger.
//
// Apparait :
//  - user authentifie
//  - jamais soumis un avis (n'importe quel context)
//  - jamais dismiss / snooze ce banner (localStorage)
//
// Disparait :
//  - apres clic "Donner mon avis" (prompt ouvert)
//  - apres clic "Plus tard" (snooze 14 jours)
//  - apres clic X (dismiss definitif)

import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { triggerAppFeedback, hasBeenPrompted } from './AppFeedbackPrompt';
import { API_BASE_URL } from '../../config/api';

// Track event en BDD analytics_events — non-bloquant.
function trackBannerEvent(name: string) {
  try {
    const token = localStorage.getItem('glowp_access_token');
    fetch(`${API_BASE_URL}/api/v1/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        name,
        page: typeof window !== 'undefined' ? window.location.pathname : '',
        properties: { source: 'existing_user_banner' },
      }),
    }).catch(() => {
      /* silent */
    });
  } catch {
    /* silent */
  }
}

const LS_SNOOZE = 'glowp_feedback_banner_snoozed_until';
const LS_DISMISSED = 'glowp_feedback_banner_dismissed';

const ExistingUserFeedbackBanner: React.FC = () => {
  const { user } = useAuth();
  // Tous les useState groupes en haut pour clarte + safety React #310
  const [closed, setClosed] = useState(false);
  const [mountDelayPassed, setMountDelayPassed] = useState(false);
  const [tracked, setTracked] = useState(false);

  // Delai 8s apres mount avant d'apparaitre (laisse user decouvrir le contenu)
  useEffect(() => {
    const t = setTimeout(() => setMountDelayPassed(true), 8000);
    return () => clearTimeout(t);
  }, []);

  const shouldShow = useMemo(() => {
    if (!user || closed) return false;
    if (!mountDelayPassed) return false;
    // Pas afficher pendant les 5 premieres minutes apres inscription :
    // l'onboarding modal est ouvert et masque le banner. On attend que
    // l'user finisse l'onboarding pour ne pas etre noye dans le bruit.
    if (user.createdAt) {
      const createdAt = new Date(user.createdAt).getTime();
      if (Date.now() - createdAt < 5 * 60 * 1000) return false;
    }
    // Si l'user a deja soumis un avis (n'importe quel context), on n'insiste pas.
    if (
      hasBeenPrompted('SPONTANEOUS') ||
      hasBeenPrompted('FIRST_PUBLISH') ||
      hasBeenPrompted('SHOP_CUSTOMIZED') ||
      hasBeenPrompted('FREQUENT_VISIT')
    ) {
      return false;
    }
    // Dismiss definitif / snooze respectes
    try {
      if (localStorage.getItem(LS_DISMISSED) === '1') return false;
      const snoozedUntil = localStorage.getItem(LS_SNOOZE);
      if (snoozedUntil && parseInt(snoozedUntil, 10) > Date.now()) return false;
    } catch {
      /* silent */
    }
    return true;
  }, [user, closed, mountDelayPassed]);

  // Au prochain ouverture du prompt SPONTANEOUS depuis ce banner, on
  // referme automatiquement (markPrompted sera set par le prompt).
  useEffect(() => {
    const handler = () => setClosed(true);
    window.addEventListener('glowp:openFeedback', handler);
    return () => window.removeEventListener('glowp:openFeedback', handler);
  }, []);

  // Track 'shown' une seule fois quand le banner devient effectivement visible
  useEffect(() => {
    if (shouldShow && !tracked) {
      trackBannerEvent('app_feedback_banner_shown');
      setTracked(true);
    }
  }, [shouldShow, tracked]);

  if (!shouldShow) return null;

  const handleGive = () => {
    trackBannerEvent('app_feedback_banner_clicked');
    triggerAppFeedback('SPONTANEOUS');
    setClosed(true);
  };

  const handleSnooze = () => {
    trackBannerEvent('app_feedback_banner_snoozed');
    try {
      localStorage.setItem(
        LS_SNOOZE,
        String(Date.now() + 14 * 24 * 60 * 60 * 1000)
      );
    } catch {
      /* silent */
    }
    setClosed(true);
  };

  const handleDismiss = () => {
    trackBannerEvent('app_feedback_banner_dismissed');
    try {
      localStorage.setItem(LS_DISMISSED, '1');
    } catch {
      /* silent */
    }
    setClosed(true);
  };

  return (
    <div
      className="fixed left-4 right-4 md:bottom-6 md:left-auto md:right-6 md:max-w-[400px] z-40 rounded-2xl shadow-2xl border-2 bg-white p-5 animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{
        borderColor: 'var(--brand-500)',
        bottom: 'max(80px, calc(env(safe-area-inset-bottom, 0px) + 80px))',
      }}
      role="dialog"
      aria-label="Marie-Amelie te demande son avis"
    >
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-lg hover:bg-gray-100 text-gray-300 hover:text-gray-500 transition-colors"
        aria-label="Fermer"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* Signature Marie-Amelie : humanise le banner. L'user voit que
          c'est une vraie personne qui demande, pas un robot. */}
      <div className="flex items-center gap-2.5 mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-sm shadow-sm"
          style={{ backgroundColor: 'var(--brand-500)' }}
        >
          MA
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-brand-warm leading-tight">
            Marie-Amelie, fondatrice Glowp
          </p>
          <p className="text-xs text-brand-muted leading-tight">
            t'a ecrit un message
          </p>
        </div>
      </div>

      <div>
        <p className="text-base font-bold text-brand-dark leading-snug">
          Tu peux me dire 2 mots ?
        </p>
        <p className="text-sm text-brand-warm mt-1.5 leading-relaxed">
          Tu es parmi les premieres a vivre Glowp. Ton ressenti, bon ou pas,
          c'est ce qui va le faire grandir. 30 secondes, c'est tout.
        </p>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          type="button"
          onClick={handleGive}
          className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-[0.98] shadow-md"
          style={{ backgroundColor: 'var(--brand-500)' }}
        >
          Avec plaisir
        </button>
        <button
          type="button"
          onClick={handleSnooze}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors px-1"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
};

export default ExistingUserFeedbackBanner;
